import {
    Directive,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";

@Directive({ selector: "[raskTableKeyboardNavigation]" })
export class TableKeyboardNavigationDirective implements OnInit, OnDestroy {
    @Input() selectedCellClassName = "selected-cell";

    private readonly element = inject<ElementRef<HTMLTableElement>>(ElementRef);

    private isEdited: boolean;
    private selectedCell: HTMLTableCellElement;
    private selectedColumn = 0;
    private selectedRow = 0;

    ngOnDestroy(): void {
        this.element.nativeElement.removeEventListener("keydown", this.handleKeyDown);
        this.element.nativeElement.removeEventListener("mousedown", this.handleMouseDown);
        this.element.nativeElement.addEventListener("focusin", this.handleFocusIn);
        this.element.nativeElement.removeEventListener("focusout", this.handleFocusOut);
    }

    ngOnInit(): void {
        this.element.nativeElement.addEventListener("keydown", this.handleKeyDown);
        this.element.nativeElement.addEventListener("mousedown", this.handleMouseDown);
        this.element.nativeElement.addEventListener("focusin", this.handleFocusIn);
        this.element.nativeElement.addEventListener("focusout", this.handleFocusOut);
    }

    private getColumns(row: HTMLTableRowElement) {
        return row.querySelectorAll<HTMLInputElement>("input:not([disabled]):not([readonly])");
    }

    private getRows() {
        const body = this.element.nativeElement.tBodies.item(0);
        return body.querySelectorAll("tr");
    }

    private handleEdit(target: HTMLInputElement) {
        if(this.isEdited) {
            return;
        }
        target.value = "";
        this.isEdited = true;
    }

    private readonly handleFocusIn = (ev: FocusEvent) => {
        const target = ev.target as HTMLInputElement;
        this.isEdited = false;
        if(!target) {
            return;
        }

        this.selectedCell = target.closest("td");
        this.selectedCell.classList.toggle(this.selectedCellClassName, true);
    };

    private readonly handleFocusOut = (_: FocusEvent) => {
        if(this.selectedCell) {
            this.selectedCell.classList.toggle(this.selectedCellClassName, false);
        }
    };

    private moveCell(x: number, y: number) {
        this.selectCell(this.selectedRow + y, this.selectedColumn + x);
    }

    private readonly handleKeyDown = (ev: KeyboardEvent) => {
        let moveX = 0;
        let moveY = 0;

        // Om nyckel längd inte är 1 antar vi att det är knapptryckning av tex typen tab,ctrl, shift osv och då ska vi inte rensa kontrollen.
        if(ev.key.length !== 1) {
            this.isEdited = true;
        }

        switch(ev.key) {
            case "ArrowRight":
                moveX = 1;
                break;
            case "ArrowLeft":
                moveX = -1;
                break;
            case "ArrowUp":
                moveY = -1;
                break;
            case "Enter":
            case "ArrowDown":
                moveY = 1;
                break;
            default:
                this.handleEdit(ev.target as HTMLInputElement);
                break;
        }

        if(moveX !== 0 || moveY !== 0) {
            ev.preventDefault();
            this.moveCell(moveX, moveY);
        }

    };

    private selectCell(row: number, col: number) {
        const rows = this.getRows();
        this.selectedRow = this.limitNumberRange(row, 0, rows.length - 1);
        const columns = this.getColumns(rows[this.selectedRow]);
        this.selectedColumn = this.limitNumberRange(col, 0, columns.length - 1);

        const cell = columns.item(this.selectedColumn);
        cell.focus({ preventScroll: true });
    }

    private readonly handleMouseDown = (ev: MouseEvent) => {
        const target = ev.target as HTMLInputElement;
        if(!target) {
            return;
        }

        const rows = this.getRows();
        for(let i = 0; i < rows.length; i += 1) {
            const columns = this.getColumns(rows[i]);
            for(let j = 0; j < columns.length; j += 1) {
                if(columns[j].id === target.id) {
                    this.selectCell(i,j);
                    return;
                }
            }
        }
    };

    private limitNumberRange(value: number, min: number, max: number) {
        if(value > max) {
            value = max;
        } else if(value < min) {
            value = min;
        }
        return value;
    }
}
